import React, { useState } from "react";
import { useWarehouseContext } from "../WarehouseProvider";
import { Header } from "./Header";
import { GotoAllStepsButton } from "./GotoAllStepsButton";
import { useSteps } from "../useSteps";
import { ReactComponent as PhotoSvg } from "../svgs/photo.svg";
import { ReactComponent as GuidanceIconSvg } from "../svgs/guidance-icon.svg";
import { PhotoThumbnailsModal } from "./PhotoThumbnailsModal";
import { DropStoreModeToggle } from "./DropStoreModeToggle";
import { DropStoreModeBadge } from "./DropStoreModeBadge";
import { PackageLookup } from "../PackageLookup";
import { RejectedBanner } from "./RejectedBanner";
import { PhotoGuidance } from "./PhotoGuidance";

export const StepHeader = ({
  leftSlot,
  rightSlot,
  isLoading,
  isCameraActive,
  isScanning,
  showUserIcon,
  isPackageLookup,
  ...props
}) => {
  const {
    sellerDisplayName,
    caseId,
    currentTakeback,
    packagePictures,
    contentPictures,
    nonConformPictures,
    scaleReadingPictures,
    deletePackagePicture,
    deleteContentPicture,
    deleteNonConformPicture,
    deleteScaleReadingPicture,
  } = useWarehouseContext();
  const { StepComponent, checkInSteps, stepIndex } = useSteps();
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showGuidance, setShowGuidance] = useState(false);

  // Show GotoAllStepsButton when processing a takeback (currentTakeback exists)
  // Don't show it during check-in process
  const showGotoAllSteps =
    currentTakeback && !rightSlot && checkInSteps.length === 0;

  // Use checkInSteps component if available, otherwise use StepComponent
  const CurrentStepComponent =
    checkInSteps.length > 0 ? checkInSteps[stepIndex] : StepComponent;

  // Only show step title for Package Photo, Conform Photo, Photo Non-Conform, and Scale Reading Photo
  const shouldShowStepTitle =
    CurrentStepComponent?.title === "Package Photo" ||
    CurrentStepComponent?.title === "Conform Photo" ||
    CurrentStepComponent?.title === "Photo Non-Conform Overview" ||
    CurrentStepComponent?.title === "Photo Scale Reading";

  // Determine which photos to show based on current step
  const isPackagePhotosStep = CurrentStepComponent?.title === "Package Photo";
  const isContentPhotosStep = CurrentStepComponent?.title === "Conform Photo";
  const isNonConformPhotosStep =
    CurrentStepComponent?.title === "Photo Non-Conform Overview";
  const isScaleReadingStep =
    CurrentStepComponent?.title === "Photo Scale Reading";
  const showPhotoIcon =
    isPackagePhotosStep ||
    isContentPhotosStep ||
    isNonConformPhotosStep ||
    isScaleReadingStep;

  // Show guidance icon only on specific photo capture pages
  const showGuidanceIcon =
    isPackagePhotosStep || isNonConformPhotosStep || isScaleReadingStep;

  const photos = isPackagePhotosStep
    ? packagePictures
    : isContentPhotosStep
      ? contentPictures
      : isNonConformPhotosStep
        ? nonConformPictures
        : scaleReadingPictures;
  const deletePhoto = isPackagePhotosStep
    ? deletePackagePicture
    : isContentPhotosStep
      ? deleteContentPicture
      : isNonConformPhotosStep
        ? deleteNonConformPicture
        : deleteScaleReadingPicture;

  // Get reject category from takeback data
  const takebackData =
    typeof currentTakeback?.data === "object"
      ? currentTakeback?.data
      : JSON.parse(currentTakeback?.data || "{}");
  const rejectCategory = takebackData?.rejectCategory;

  // Get tracking number from shippingss if available
  const trackingNumber =
    currentTakeback?.shippingss?.items?.[0]?.tracking_number ??
    currentTakeback?.trackingId; // for fake takebacks (fullX packages)

  const storeName = currentTakeback?.data?.returnTypeOffered;

  const handleCopyId = (id, type) => {
    navigator.clipboard.writeText(id);
    setCopiedId(type);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Don't show rejection banner in photo capture steps - they handle it separately
  const showRejectionBanner =
    rejectCategory &&
    !isPackagePhotosStep &&
    !isContentPhotosStep &&
    !isNonConformPhotosStep &&
    !isScaleReadingStep;

  return (
    <>
      <Header
        {...props}
        isCameraActive={isCameraActive}
        showUserIcon={showUserIcon}
      >
        <div className="flex justify-center items-center">
          <div className="flex flex-col items-center gap-1 w-full">
            {!isPackageLookup && (
              <span className="font-bold text-2xl text-white">
                {isScanning
                  ? "Looking for shipping label barcode..."
                  : storeName
                    ? storeName
                    : shouldShowStepTitle
                      ? CurrentStepComponent.title
                      : sellerDisplayName}
              </span>
            )}
            {(caseId || trackingNumber) && !isScanning && !isPackageLookup && (
              <span
                className={`font-mono text-base leading-5 relative ${isCameraActive ? "text-white" : "text-[#C6CCE8]"}`}
              >
                {caseId && (
                  <span
                    className="cursor-pointer hover:text-white transition-colors relative"
                    onClick={() => handleCopyId(caseId, "case")}
                  >
                    ID: {caseId}
                    {copiedId === "case" && (
                      <span className="absolute -top-6 left-0 text-green-400 text-xs bg-black/80 px-2 py-1 rounded">
                        Copied!
                      </span>
                    )}
                  </span>
                )}
                {caseId && trackingNumber && " • "}
                {trackingNumber && (
                  <span
                    className="cursor-pointer hover:text-white transition-colors relative"
                    onClick={() => handleCopyId(trackingNumber, "tracking")}
                  >
                    SHIPPING ID: {trackingNumber}
                    {copiedId === "tracking" && (
                      <span className="absolute -top-6 left-0 text-green-400 text-xs bg-black/80 px-2 py-1 rounded">
                        Copied!
                      </span>
                    )}
                  </span>
                )}
              </span>
            )}
          </div>
          {leftSlot && <div className="ml-auto">{leftSlot}</div>}
          <div className="ml-auto flex items-center gap-4">
            {!isScanning &&
              (CurrentStepComponent === PackageLookup ? (
                <div className="mr-2 order-2">
                  <DropStoreModeToggle disabled={isLoading} />
                </div>
              ) : (
                <DropStoreModeBadge />
              ))}
            {showGuidanceIcon && (
              <button
                onClick={() => setShowGuidance(true)}
                className="ml-4 hover:opacity-80 transition-opacity"
              >
                <GuidanceIconSvg
                  className={`w-8 h-8 ${isCameraActive ? "pulsate-white" : "pulsate-gray"}`}
                />
              </button>
            )}
            {rightSlot ||
              (showGotoAllSteps && (
                <GotoAllStepsButton isCameraActive={isCameraActive} />
              ))}
            {showPhotoIcon && photos?.length > 0 && (
              <button
                onClick={() => setShowPhotoModal(true)}
                className="relative px-2 mr-2 ml-2 hover:bg-[#282F44]/20 rounded-lg transition-colors aspect-square"
                disabled={photos.length === 0}
              >
                <PhotoSvg
                  className={`w-6 ${isCameraActive ? "fill-white" : "fill-[#A1A7C2]"}`}
                />
                <sup className="absolute top-[unset] bottom-[0.5rem] flex items-center justify-center aspect-square right-0 rounded-full w-4 h-4 bg-[#C12960] text-white text-xs text-center">
                  {photos.length}
                </sup>
              </button>
            )}
          </div>
        </div>
      </Header>
      {showRejectionBanner && (
        <RejectedBanner rejectCategory={rejectCategory} />
      )}

      <PhotoThumbnailsModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        photos={photos}
        onDeletePhoto={deletePhoto}
        title={`${CurrentStepComponent?.title || ""} Gallery`}
      />

      {showGuidance && (
        <PhotoGuidance
          onClose={() => setShowGuidance(false)}
          currentStep={CurrentStepComponent?.title || ""}
        />
      )}
    </>
  );
};
